import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { brokerCredentialsService, type BuilderFeeStatus } from '@/services/brokerCredentials';
import { injectedWallet, signTypedDataV4, WalletError } from '@/lib/walletSign';

/****************
 * CopyFi's fee, approved from the person's own wallet. 2026-11-03.
 *
 * Guillaume: "Do users have to run a CMD? This is VERY inconvenient." They do
 * not: one button, the wallet shows the rate and asks, and the signature goes
 * to the server, which checks it and sends it on (backend builder_fee.go).
 *
 * Shows nothing at all until CopyFi has set a fee (the server answers
 * available:false), so deploying this changes no screen. The fee is never a
 * condition of using CopyFi, and the text says so.
 ****************/
export function BuilderFeeApproval({ credentialId }: { credentialId: string }) {
  const { t } = useTranslation('copy');
  const { toast } = useToast();
  const [status, setStatus] = useState<BuilderFeeStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus(await brokerCredentialsService.getBuilderFee(credentialId));
  }, [credentialId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = async (action: 'approve' | 'revoke') => {
    setMessage(null);
    const wallet = injectedWallet();
    if (!wallet) {
      setMessage(t('broker.fee.noWallet'));
      return;
    }
    setBusy(true);
    try {
      const prepared = await brokerCredentialsService.prepareBuilderFee(credentialId, action);
      const sig = await signTypedDataV4(wallet, prepared.account, prepared.typed_data);
      const out = await brokerCredentialsService.submitBuilderFee(credentialId, { action, nonce: prepared.nonce, ...sig });
      if (out.status) setStatus(out.status);
      else await load();
      toast({ title: t(action === 'approve' ? 'broker.fee.done' : 'broker.fee.withdrawn') });
    } catch (e) {
      if (e instanceof WalletError) {
        if (e.code === 'no_wallet') setMessage(t('broker.fee.noWallet'));
        else if (e.code === 'wrong_account') setMessage(t('broker.fee.wrongAccount', { address: e.detail ?? '' }));
        else if (e.code === 'rejected') setMessage(t('broker.fee.rejected'));
        else setMessage(t('broker.fee.failed', { reason: e.detail ?? '' }));
      } else {
        setMessage(t('broker.fee.failed', { reason: e instanceof Error ? e.message : String(e) }));
      }
    } finally {
      setBusy(false);
    }
  };

  if (!status?.available) return null;

  return (
    <div className="mt-2 space-y-1 rounded-md border p-2" data-testid="builder-fee">
      <p className="text-xs font-medium">{t('broker.fee.title')}</p>
      <p className="text-xs text-muted-foreground">{t('broker.fee.explain', { rate: status.rate })}</p>
      {status.approved ? (
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-xs">{t('broker.fee.approved', { rate: status.rate })}</p>
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => void run('revoke')}>
            {busy ? t('broker.fee.working') : t('broker.fee.withdraw')}
          </Button>
        </div>
      ) : (
        <Button type="button" size="sm" disabled={busy} onClick={() => void run('approve')}>
          {busy ? t('broker.fee.working') : t('broker.fee.approve', { rate: status.rate })}
        </Button>
      )}
      {message && <p className="text-xs text-destructive">{message}</p>}
    </div>
  );
}
